import { ApexOptions } from 'apexcharts'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import ReactApexChart from 'react-apexcharts'
import { baseChartOptions, formatAxisNumber, mergeOptions } from './charts/chartTheme'

export interface IChartSeries {
    name: string
    data: number[]
}

interface ILineChart {
    categories: string[]
    series: IChartSeries[]
    /** Full labels for tooltips (defaults to categories) */
    tooltipLabels?: string[]
    unit?: string
    xTitle?: string
    yTitle?: string
    type?: 'area' | 'line'
    color?: string
}

/** Below this chart width, DD/MM labels are shortened to D/M. */
const NARROW_WIDTH = 480
/** Approximate horizontal space (px) one x-axis label needs at 12px, incl. gap. */
const LABEL_SLOT_WIDE = 48
const LABEL_SLOT_NARROW = 38
/** Space taken by the y-axis labels/title and grid padding. */
const Y_AXIS_GUTTER = 64

/** "05/03" -> "5/3"; leaves non-numeric labels (e.g. "3月") untouched. */
const shortenLabel = (label: string) => label.replace(/(^|\/)0(\d)/g, '$1$2')

const useElementWidth = () => {
    const ref = useRef<HTMLDivElement>(null)
    const [width, setWidth] = useState(0)
    useEffect(() => {
        const el = ref.current
        if (!el) return
        setWidth(el.clientWidth)
        if (typeof ResizeObserver === 'undefined') return
        const observer = new ResizeObserver((entries) => {
            const next = Math.round(entries[0]?.contentRect.width ?? 0)
            setWidth((prev) => (prev === next ? prev : next))
        })
        observer.observe(el)
        return () => observer.disconnect()
    }, [])
    return { ref, width }
}

export const LineChart: FC<ILineChart> = ({ categories, series, tooltipLabels, unit = '', xTitle, yTitle, type = 'area', color }) => {
    const { ref, width } = useElementWidth()
    const narrow = width > 0 && width < NARROW_WIDTH

    // Pick an even label step ourselves so every visible label renders in full
    // (ApexCharts' tickAmount + trim truncated DD/MM to "05..." or ".").
    const labelStep = useMemo(() => {
        if (!width || categories.length === 0) return 1
        const slot = narrow ? LABEL_SLOT_NARROW : LABEL_SLOT_WIDE
        const maxLabels = Math.max(2, Math.floor((width - Y_AXIS_GUTTER) / slot))
        return Math.max(1, Math.ceil(categories.length / maxLabels))
    }, [width, narrow, categories.length])

    const options = useMemo<ApexOptions>(() => {
        const indexOf = new Map(categories.map((c, i) => [c, i]))
        return mergeOptions(baseChartOptions, {
            ...(color ? { colors: [color] } : {}),
            // Monotone curve never overshoots between points (daily counts are integers >= 0).
            stroke: { curve: 'monotoneCubic' },
            fill: type === 'area'
                ? { type: 'solid', opacity: 0.12 }
                : { type: 'solid', opacity: 1 },
            markers: { size: categories.length > 16 ? 0 : 3, strokeWidth: 0, hover: { size: 5 } },
            legend: { show: series.length > 1 },
            xaxis: {
                type: 'category',
                categories,
                tickAmount: undefined,
                labels: {
                    trim: false,
                    hideOverlappingLabels: false,
                    rotate: 0,
                    formatter: (value: string) => {
                        if (value === undefined || value === null) return ''
                        const label = String(value)
                        const i = indexOf.get(label)
                        if (i !== undefined && i % labelStep !== 0) return ''
                        return narrow ? shortenLabel(label) : label
                    },
                },
                title: { text: xTitle },
            },
            yaxis: { min: 0, forceNiceScale: true, title: { text: yTitle } },
            tooltip: {
                x: { formatter: (_value: number, opts?: { dataPointIndex: number }) => (tooltipLabels ?? categories)[opts?.dataPointIndex ?? 0] ?? '' },
                y: { formatter: (value: number) => `${formatAxisNumber(value)} ${unit}`.trim() },
            },
        })
    }, [categories, series.length, tooltipLabels, unit, xTitle, yTitle, type, color, labelStep, narrow])

    return <div ref={ref} className='h-full w-full min-w-0'>
        <ReactApexChart options={options} series={series} type={type} width='100%' height='100%' />
    </div>
}
